import { useBrandPage } from "./useBrandL2";

import { useParams } from "@solidjs/router";
import { Match, Show, Switch } from "solid-js";
import { HubbleAssured } from "~/components/brand/assured";
import { SecondaryCtas } from "~/components/brand/secondary_ctas";
import { AboutTheBrand } from "~/components/brand_l2/about_amazon";
import { AboutThisVoucher } from "~/components/brand_l2/about_this_card";
import { BrandTitle } from "~/components/brand_l2/brandTitle";
import { DenominationsGrid } from "~/components/brand_l2/denominations_grid";
import { Faqs } from "~/components/brand_l2/faqs";
import { SimilarBrands } from "~/components/brand_l2/similar_brands";
import { TrustMarkerHappyUsers } from "~/components/brand_l2/trust_marker_happy_users";
import { VoucherGenerationBrandCard } from "~/components/brand_l2/voucher_generation_brand_card";
import { WhatsappTip } from "~/components/brand_l2/whatsapp_tip";
import { Footer } from "~/components/footer";

import { Mode } from "~/types";
import { Divider } from "~/widgets/divider";
import { Spacer } from "~/widgets/spacer";
import { Link, Meta, Title } from "@solidjs/meta";
import { getCategorySlugFromCategoryName } from "~/utils/common";
import { SeoSchema, MetadataTags } from "~/components/brand_l2/seo";
import HubbleImage from "~/widgets/hubble_image";
import BrandL2FooterNotes from "~/components/brand_l2/footer_notes";
import { BreadcrumbsNav } from "~/widgets/breadcrumbs";
import { config } from "~/data/config";

import { getSimulateTransactionDetails } from "~/utils/brandl2";
import { clientIdConfig } from "~/data/partner_config";
import { BottomStickyBranding$Client } from "~/components/partner_components/partner_bottom_page_branding";

export default function BrandPageComponent(props: {
  brandPageData: ReturnType<typeof useBrandPage>;
}) {
  const { brandPageData } = props;

  const params = useParams();

  const {
    routeData,
    getUIConfig,
    getMode,
    isLoggedIn,
    getClientId,
    getMetaTitle,
    onClickGetThisCard,
    redemptionControls,
  } = brandPageData;

  function getHomePageUrl() {
    return config.paths.homePageUrl;
  }

  function getCategoryPageBasePath() {
    return config.paths.categoryPageBasePath;
  }

  return (
    <div class="flex flex-col items-center justify-center gap-6">
      <div class="m-auto mt-3 flex flex-row lg:mt-5 lg:w-[1011px] lg:gap-10">
        <div class="max-w-[573px] lg:ml-0 lg:mr-0">
          <div class="mx-4 flex flex-col gap-0.5 lg:mx-0">
            <Meta name="robots" content="follow, index" />
            <Title>{getMetaTitle()}</Title>
            <Link
              rel="canonical"
              href={`https://offers.myhubble.money/buy-gift-card/${params.brandKey}`}
            />
            <Meta name="og:type" content="product" />
            <Meta name="og:title" content={getMetaTitle()} />
            <Meta
              name="description"
              content={`${routeData()!.brandExtraMetadata.voucherDescription}`}
            />
            <Meta
              name="og:description"
              content={`${routeData()!.brandExtraMetadata.voucherDescription}`}
            />
            <Meta
              name="og:image"
              content={`${routeData()!.brand.voucherProductMetadata.iconImageUrl}`}
            />
            <MetadataTags routeData={routeData} />
            <SeoSchema routeData={routeData} />
            <Show
              when={getUIConfig().showBreadcrumbsNav}
              fallback={<Spacer height={30} />}
            >
              <BreadcrumbsNav
                breadcrumbs={[
                  {
                    label: "HOME",
                    href: getHomePageUrl(),
                  },
                  ...(!config.customizedSDKClients.limitedCategoryContent.includes(
                    routeData()?.clientId!
                  )
                    ? [
                        {
                          label:
                            routeData()!.brandExtraMetadata.categoryTitle?.toUpperCase() ??
                            "SHOP",
                          href: routeData()!.brandExtraMetadata.categoryName
                            ? getCategoryPageBasePath() +
                              getCategorySlugFromCategoryName(
                                routeData()!.brandExtraMetadata.categoryName
                              )
                            : getHomePageUrl(),
                        },
                      ]
                    : []),
                  {
                    label:
                      routeData()!.brand.voucherProductMetadata.title.toUpperCase(),
                    href: "#",
                    active: true,
                  },
                ]}
              />
            </Show>
            <BrandTitle
              brandName={routeData()!.brand.voucherProductMetadata.title}
              discountPercentage={
                getSimulateTransactionDetails(routeData)?.discountDetails
                  .hubbleBaseDiscount.percentage!
              }
              showShoppingVoucher={false}
              showPercOff={routeData()!.brand.rewardType != "POSTPAID"}
            />
            <Show
              when={routeData()!.brand.voucherProductMetadata?.headingSubtitle}
            >
              <h1 class="line-clamp-1 pt-2 text-normal text-textNormal">
                {routeData()!.brand.voucherProductMetadata?.headingSubtitle}
              </h1>
            </Show>
          </div>
          <Spacer height={20} />
          <div class="flex flex-col gap-5 lg:gap-6">
            <Show
              when={routeData()!.brand.voucherProductMetadata.isVoucherAssured}
            >
              <div class="mx-4 lg:hidden">
                <HubbleAssured />
              </div>
            </Show>

            <div
              class="flex flex-col gap-4 bg-baseTertiaryMedium p-4 lg:p-0"
              style={
                routeData()?.clientConfig &&
                (routeData()!.brand.name == "Amazon" ||
                  routeData()!.brand.name == "Amazon shopping")
                  ? {
                      "border-radius": "20px",
                    }
                  : { "border-radius": "24px" }
              }
            >
              <Switch>
                <Match
                  when={routeData()?.brand.voucherProductMetadata.heroImageUrl}
                >
                  <HubbleImage
                    class="aspect-[541/316] w-full rounded-3xl lg:h-[334px]"
                    src={routeData()!.brand.voucherProductMetadata.heroImageUrl}
                    alt={routeData()!.brand.voucherProductMetadata.title}
                  />
                </Match>
                <Match when={true}>
                  <VoucherGenerationBrandCard
                    plainLogoUrl={
                      routeData()!.brand.voucherProductMetadata.plainLogoUrl
                    }
                    cardBackgroundColor={
                      routeData()!.brand.voucherProductMetadata
                        .cardBackgroundColor
                    }
                    showHubbleLogo={getUIConfig().showHubbleLogoOnBrandCard}
                  />
                </Match>
              </Switch>
              <div class="lg:hidden">{buildDenominationGrid()}</div>
            </div>

            <div class="mx-4 flex flex-col gap-5 lg:mx-0">
              <SecondaryCtas
                howToUseSteps={
                  routeData()!.brand.voucherProductMetadata.howToUseSteps
                }
                tncResId={routeData()!.brand.voucherProductMetadata.tncResId}
              />
              <Show when={getUIConfig().showWhatsappTip}>
                <div class="lg:hidden">
                  <WhatsappTip />
                </div>
              </Show>
            </div>

            <div class="mx-[18px] flex flex-col gap-7 lg:mx-2 lg:gap-8">
              <Divider />
              <AboutThisVoucher
                voucherDescription={
                  routeData()!.brandExtraMetadata.voucherDescription
                }
                voucherHighlights={
                  routeData()!.brandExtraMetadata.voucherHighlights
                }
              />
              <Show when={getUIConfig().showHubbleTrustMarker}>
                <Divider />
                <TrustMarkerHappyUsers />
              </Show>
              <Show when={routeData()!.brandExtraMetadata.brandDescription}>
                <Divider />
                <AboutTheBrand
                  brandName={routeData()!.brand.voucherProductMetadata.title}
                  brandDescription={
                    routeData()!.brandExtraMetadata.brandDescription
                  }
                  thumbnailUrl={
                    routeData()!.brand.voucherProductMetadata.iconImageUrl
                  }
                />
              </Show>

              <Show when={routeData()!.brandExtraMetadata.faqs.length > 0}>
                <Divider />
                <Faqs
                  faqs={routeData()!.brandExtraMetadata.faqs}
                  headerText="FAQs"
                />
              </Show>
              <Show
                when={
                  routeData()?.sdkMetaData?.mode !== "sdk" &&
                  routeData()!.brandExtraMetadata.similarBrands.length > 0 &&
                  getClientId() !== clientIdConfig.tdClientId
                }
              >
                <Divider />
                <SimilarBrands
                  mode={routeData()?.mode as Mode}
                  similarBrands={routeData()!.brandExtraMetadata.similarBrands.slice(
                    0,
                    20
                  )}
                />
              </Show>
            </div>
          </div>
        </div>

        <div class="hidden lg:flex lg:w-[398px] lg:flex-col">
          <Spacer height={18} />
          <div
            class="gap-5 lg:sticky lg:flex lg:flex-col"
            classList={{
              "lg:top-[92px]":
                routeData()!.brand.voucherProductMetadata.isVoucherAssured,
              "lg:top-[20px]":
                !routeData()!.brand.voucherProductMetadata.isVoucherAssured &&
                !(getClientId() === clientIdConfig.axisClientId),
              "lg:top-10": getClientId() === clientIdConfig.axisClientId,
            }}
          >
            <Show
              when={routeData()!.brand.voucherProductMetadata.isVoucherAssured}
              fallback={<Spacer height={52} />}
            >
              <HubbleAssured />
            </Show>
            {buildDenominationGrid()}
            <Show when={getUIConfig().showWhatsappTip}>
              <WhatsappTip />
            </Show>
          </div>
        </div>
      </div>
      <Show when={getUIConfig().showFooterNotes}>
        <BrandL2FooterNotes
          footerList={routeData()!.brandExtraMetadata.footNotes}
        />
      </Show>
      <Show when={getUIConfig().showFooter} fallback={<Spacer height={40} />}>
        <div class="lg:px-40">
          <Footer />
        </div>
      </Show>
      <Show when={getUIConfig().showBrandingOnBottom}>
        <div
          class={`fixed bottom-[-2px] left-0 right-0 z-[20] bg-baseTertiaryLight`}
        >
          <BottomStickyBranding$Client clientId={() => routeData()?.clientId} />
        </div>
      </Show>
    </div>
  );

  // todo: denominations grid can manage it states independently, just by accepting a simulate function and similar functions
  function buildDenominationGrid() {
    return (
      <DenominationsGrid
        discountPercentage={
          getSimulateTransactionDetails(routeData)?.discountDetails
            ?.totalDiscount?.percentage ?? 0
        }
        initialErrorOnSimulate={redemptionControls.initialErrorOnSimulate}
        dryRunErrorMessage={redemptionControls.dryRunErrorMessage}
        selectedAmount={redemptionControls.selectedAmount}
        setSelectedAmount={redemptionControls.setSelectedAmount}
        selectedVariantId={redemptionControls.selectedVariantId}
        setSelectedVariantId={redemptionControls.setSelectedVariantId}
        isSimulating={redemptionControls.isSimulating}
        initiateOrder={onClickGetThisCard}
        useRewardCoins={redemptionControls.useRewardCoins}
        setUseRewardCoins={redemptionControls.setUseRewardCoins}
        ctaState={redemptionControls.ctaState}
        allowRewardCoins={getMode() === "hubble" && isLoggedIn}
        showHubbleCoinRewards={
          routeData()?.brand.rewardType === "POSTPAID" && getMode() === "hubble"
        }
        hubbleCoinRewards={redemptionControls.hubbleCoinRewards}
        dueAmount={redemptionControls.dueAmount}
        discountDetails={redemptionControls.discountDetails}
        discountDetails2={redemptionControls.discountDetails2}
        redemptionAmount={redemptionControls.redemptionAmount}
        dueAmount2={redemptionControls.dueAmount2}
        redemptionAmount2={redemptionControls.redemptionAmount2}
        offerConstructTenure={redemptionControls.offerConstructTenure}
        setOfferConstructTenure={redemptionControls.setOfferConstructTenure}
        brand={routeData()!.brand}
        isCoinsOnly={
          getSimulateTransactionDetails(routeData)?.paymentSource ===
          "COINS_ONLY"
        }
        coinsBalance={routeData()?.coinsSummary?.totalAvailable ?? 0}
        rewardDiscountAmount={redemptionControls.rewardDiscountAmount}
        offerConstructMaxAmount={
          routeData()?.offerConstruct?.oneTimeRewardDetails.maxAmount
        }
        offerConstructAmountUnit={
          routeData()?.offerConstruct?.oneTimeRewardDetails.amountUnit
        }
        offerConstruct={routeData()?.offerConstruct}
        isRewardTypePostPaid={routeData()!.brand.rewardType === "POSTPAID"}
        dividerClass="px-4 lg:px-6"
        minAmount={routeData()!.brand.amountConditions.minAmount}
        maxAmount={routeData()!.brand.amountConditions.maxAmount}
      />
    );
  }
}
